import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Button, Tooltip, IconButton,
  Typography,
  Grid,
  Card, CardContent, CardHeader,
  Checkbox, FormControlLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { useTranslation } from 'react-i18next';
import TagsDropdown from '../TagsDropdown';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import TagEditor from '../Widgets/TagEditor';
import { useFolderRestrictions } from '../useFolderRestrictions';
import { useFolderPicker } from '../useFolderPicker';
import { normalizeTagVariable } from '../../Utils/tagUtils';

export const normalizeOutputTagName = normalizeTagVariable;

const normalizeTagPromptValues = (prompts, fallback = {}) => {
  const sourcePrompts = Array.isArray(prompts) && prompts.length > 0
    ? prompts
    : [{
      tag: fallback.tag,
      prompt: fallback.prompt ?? fallback.instructions,
    }];

  return sourcePrompts.map((tagPrompt) => ({
    tag: normalizeOutputTagName(tagPrompt?.tag),
    prompt: String(tagPrompt?.prompt ?? ''),
  }));
};

export const parseGenerativeAnswerData = (data = {}) => {
  const sourceData = data?.customData !== undefined && data?.customData !== null
    ? data.customData
    : data?.data;

  if (typeof sourceData === 'string') {
    try {
      const parsedData = JSON.parse(sourceData);
      return parsedData && typeof parsedData === 'object' && !Array.isArray(parsedData)
        ? parsedData
        : {};
    } catch (_error) {
      return {};
    }
  }

  return sourceData && typeof sourceData === 'object' && !Array.isArray(sourceData)
    ? sourceData
    : {};
};

export const areGenerativeAnswerSettingsUnchanged = ({
  initialData,
  context,
  useEventWidgetContext,
  tagPrompts,
  filePath,
  actionType,
}) => {
  if (!initialData) return false;

  const initialContext = initialData.context || '';
  const initialUseEventWidgetContext = initialData.useEventWidgetContext !== undefined
    ? Boolean(initialData.useEventWidgetContext)
    : true;
  const initialTagPrompts = normalizeTagPromptValues(initialData.tagPrompts, {
    tag: initialData.tag,
    instructions: initialData.instructions,
  });
  const currentTagPrompts = normalizeTagPromptValues(tagPrompts);
  const filePathUnchanged = actionType !== 'generativeanswerfile'
    || String(filePath || '').trim() === String(initialData.filePath || '').trim();

  return context === initialContext
    && useEventWidgetContext === initialUseEventWidgetContext
    && JSON.stringify(currentTagPrompts) === JSON.stringify(initialTagPrompts)
    && filePathUnchanged;
};

const ActionsGenerativeAnswer = ({
  caseid,
  tags,
  settingId,
  onSaveStatusChange,
  actionType = 'generativeanswer',
  actionTitle
}) => {
  const { t } = useTranslation();
  const [context, setContext] = useState('');
  const [useEventWidgetContext, setUseEventWidgetContext] = useState(true);
  const [filePath, setFilePath] = useState('');
  const [filePathError, setFilePathError] = useState('');
  const [fileBrowseDisabled, setFileBrowseDisabled] = useState(false);
  const [tagPrompts, setTagPrompts] = useState([
    { id: Date.now(), tag: '', prompt: '' }
  ]);
  const [genAnchorEl, setGenAnchorEl] = useState(null);
  const [genMenuType, setGenMenuType] = useState('');
  const [activeTagIndex, setActiveTagIndex] = useState(null);

  const contextRef = useRef(null);
  const filePathRef = useRef(null);
  const promptRefs = useRef({});
  const tagPromptRefs = useRef({});
  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);
  const allowedRootsText = restrictToUserFolder
    ? (allowedRoots?.length ? allowedRoots.join(', ') : (displayRoot || t('generative_answer.file_path_root_label', 'your project folder')))
    : '';
  const filePathHelperText = filePathError
    ? filePathError
    : (restrictToUserFolder
        ? t('generative_answer.file_path_help', 'Choose a file from this allowed folder: {allowed}', { allowed: allowedRootsText })
        : t('generative_answer.file_path_help_any', 'Choose the file you want the assistant to read.')
      );
  const { handleBrowseDirectory: openFileBrowser, FolderBrowserComponent: FileBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (selectedPath) => {
      if (selectedPath) {
        setFilePath(selectedPath);
        if (filePathError) {
          setFilePathError('');
        }
      }
      setFileBrowseDisabled(false);
    },
    onFolderBrowserClose: () => {
      setFileBrowseDisabled(false);
    }
  });
  const handleFileBrowse = useCallback(() => {
    setFileBrowseDisabled(true);
    openFileBrowser();
  }, [openFileBrowser]);

  // Unified action settings hook
  const resolvedActionTitle = actionTitle || t('workflow.generativeanswer', 'Ajout Smart Tag');

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    handleConditionsChange,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
    updateSaveDisabledState
  } = useActionSettings({
    settingId,
    caseid,
    actionType,
    onSaveStatusChange,
    customComparison: (initialData) => {
      return areGenerativeAnswerSettingsUnchanged({
        initialData,
        context,
        useEventWidgetContext,
        tagPrompts,
        filePath,
        actionType,
      });
    },
    customSaveData: () => ({
      context,
      useEventWidgetContext,
      tagPrompts: tagPrompts.map(tp => ({
        id: tp.id,
        tag: normalizeOutputTagName(tp.tag),
        prompt: tp.prompt
      })),
      ...(actionType === 'generativeanswerfile' ? { filePath: filePath.trim() } : {}),
      useConditions: true,
      conditions
    }),
    loadDataFunction: (data) => {
      console.log('Loading generative answer data:', data);

      // Save responses may retain a stale serialized `data` field while
      // returning the current settings in `customData`.
      const parsedData = parseGenerativeAnswerData(data);
      
      console.log('Parsed data:', parsedData);
      
      // Handle context
      if (parsedData.context !== undefined) {
        setContext(parsedData.context);
      }

      setUseEventWidgetContext(
        parsedData.useEventWidgetContext !== undefined
          ? Boolean(parsedData.useEventWidgetContext)
          : true
      );
      
      if (parsedData.filePath !== undefined) {
        setFilePath(parsedData.filePath);
      } else {
        setFilePath('');
      }
      
      // Handle tagPrompts
      if (parsedData.tagPrompts && Array.isArray(parsedData.tagPrompts)) {
        const loadedTagPrompts = parsedData.tagPrompts.map((tp, index) => ({
          id: tp.id || Date.now() + index,
          tag: normalizeOutputTagName(tp.tag),
          prompt: tp.prompt || ''
        }));
        setTagPrompts(loadedTagPrompts);
      } else {
        // Legacy support for old single tag/instructions format
        const legacyTagPrompts = [{
          id: Date.now(),
          tag: normalizeOutputTagName(parsedData.tag),
          prompt: parsedData.instructions || ''
        }];
        setTagPrompts(legacyTagPrompts);
      }
    }
  });

  // Update save state when context or tagPrompts change
  useEffect(() => {
    if (updateSaveDisabledState) {
      updateSaveDisabledState();
    }
  }, [context, useEventWidgetContext, tagPrompts, filePath, updateSaveDisabledState]);

  const insertTokenToEditor = useCallback((tagValue, targetType, tagIndex = null) => {
    if (targetType === 'context') {
      contextRef.current?.insertTag(tagValue);
    } else if (targetType === 'prompt' && tagIndex !== null) {
      const promptId = tagPrompts[tagIndex]?.id;
      if (promptId) {
        promptRefs.current[promptId]?.insertTag(tagValue);
      }
    } else if (targetType === 'tag' && tagIndex !== null) {
      const promptId = tagPrompts[tagIndex]?.id;
      if (promptId) {
        tagPromptRefs.current[promptId]?.insertTag(tagValue);
      }
    } else if (targetType === 'filePath') {
      filePathRef.current?.insertTag(tagValue);
    }
  }, [tagPrompts]);

  // Helper functions for managing tag prompts
  const addTagPrompt = () => {
    const newTagPrompt = {
      id: Date.now(),
      tag: '',
      prompt: ''
    };
    setTagPrompts([...tagPrompts, newTagPrompt]);
  };

  const removeTagPrompt = (index) => {
    if (tagPrompts.length > 1) {
      const promptIdToRemove = tagPrompts[index].id;
      if (promptRefs.current[promptIdToRemove]) {
        delete promptRefs.current[promptIdToRemove];
      }
      if (tagPromptRefs.current[promptIdToRemove]) {
        delete tagPromptRefs.current[promptIdToRemove];
      }
      const updated = tagPrompts.filter((_, i) => i !== index);
      setTagPrompts(updated);
    }
  };

  const updateTagPromptTag = (index, newTag) => {
    const updated = [...tagPrompts];
    updated[index].tag = normalizeOutputTagName(newTag);
    setTagPrompts(updated);
  };

  const updateTagPromptPrompt = (index, newPrompt) => {
    const updated = [...tagPrompts];
    updated[index].prompt = newPrompt;
    setTagPrompts(updated);
  };

  // New handlers for the generative answer specific dropdown
  const handleGenMenuOpen = (event, type, tagIndex = null) => {
    setGenAnchorEl(event.currentTarget);
    setGenMenuType(type);
    setActiveTagIndex(tagIndex);
  };

  const handleGenMenuClose = () => {
    setGenAnchorEl(null);
    setActiveTagIndex(null);
  };

  const handleGenTagSelect = (value) => {
    if (genMenuType === 'tag' && activeTagIndex !== null) {
      // This field is the destination variable name, not a dynamic tag
      // expression. Store the selected variable without {braces}.
      updateTagPromptTag(activeTagIndex, value);
    } else if (genMenuType === 'context') {
      insertTokenToEditor(value, 'context');
    } else if (genMenuType === 'prompt' && activeTagIndex !== null) {
      insertTokenToEditor(value, 'prompt', activeTagIndex);
    } else if (genMenuType === 'filePath') {
      insertTokenToEditor(value, 'filePath');
    }
    handleGenMenuClose();
  };

  const handleStatusWindowClose = () => {
    handleCloseStatusWindow();
  };

  // Custom save handler that uses the customSaveData
  const handleCustomSave = () => {
    if (actionType === 'generativeanswerfile' && !filePath.trim()) {
      setFilePathError(t('generative_answer.file_path_error_required', 'Choose a file before saving.'));
      return;
    }

    if (filePathError) {
      setFilePathError('');
    }

    handleSave();
  };

  return (
    <ActionSettingsLayout
      title={resolvedActionTitle}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleCustomSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleStatusWindowClose}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
    >
      <Grid container spacing={3}>
        {/* Context Input */}
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #7b61ff10',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#7b61ff',
                    color: 'white',
                    borderRadius: '6px',
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 0.5, sm: 0.7 },
                    mr: { xs: 1, sm: 2 },
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(123, 97, 255, 0.2)',
                  }}>
                    {t('generative_answer.context_badge', 'INFO')}
                  </Box>
                  {t('generative_answer.context_label', 'What the assistant should know')}
                </Typography>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />
            <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useEventWidgetContext}
                    onChange={(event) => setUseEventWidgetContext(event.target.checked)}
                    sx={{
                      color: '#7b61ff',
                      '&.Mui-checked': {
                        color: '#7b61ff',
                      },
                    }}
                  />
                }
                label={t('generative_answer.use_event_widget_context_label', 'Use event/widget information')}
                sx={{
                  mb: 1.5,
                  '& .MuiFormControlLabel-label': {
                    fontWeight: 600,
                    color: '#444',
                  },
                }}
              />
              <TagEditor
                caseid={caseid}
                id={`gen-context-${settingId}`}
                aria-describedby={`gen-context-help-${settingId}`}
                ref={contextRef}
                value={context}
                onChange={setContext}
                placeholder={t('generative_answer.context_placeholder', 'Write the background information the assistant should keep in mind...')}
                minHeight="100px"
                onTagClick={(e) => handleGenMenuOpen(e, 'context')}
                tags={tags}
              />

              <Typography
                id={`gen-context-help-${settingId}`}
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
              >
                {t('generative_answer.context_help', 'This helps the assistant give answers that fit your project.')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {actionType === 'generativeanswerfile' && (
          <Grid item xs={12}>
            <Card sx={{
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
              border: '1px solid #7b61ff10',
              overflow: 'hidden',
            }}>
              <CardHeader
                title={
                  <Typography sx={{
                    fontWeight: 700,
                    color: '#444',
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1
                  }}>
                    <Box component="span" sx={{
                      bgcolor: '#7b61ff',
                      color: 'white',
                      borderRadius: '6px',
                      px: { xs: 1, sm: 1.5 },
                      py: { xs: 0.5, sm: 0.7 },
                      mr: { xs: 1, sm: 2 },
                      fontSize: { xs: '0.75rem', sm: '0.85rem' },
                      letterSpacing: '0.5px',
                      fontWeight: 600,
                      boxShadow: '0 3px 6px rgba(123, 97, 255, 0.2)',
                    }}>
                      {t('generative_answer.file_badge', 'FILE')}
                    </Box>
                    {t('generative_answer.file_path_label', 'File to read')}
                  </Typography>
                }
                sx={{
                  borderBottom: '1px solid #f0f0f8',
                  backgroundColor: '#fafbff',
                  borderTopLeftRadius: '12px',
                  borderTopRightRadius: '12px',
                  p: { xs: 1.5, sm: 2.5 },
                }}
              />
              <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                <Box sx={{
                  '& .generative-answer-file-path-editor': {
                    borderColor: filePathError ? 'error.main' : '#d0d0e8',
                    '&:hover': {
                      borderColor: filePathError ? 'error.main' : '#7b61ff',
                    },
                  },
                }}>
                  <TagEditor
                    caseid={caseid}
                    ref={filePathRef}
                    value={filePath}
                    onChange={(value) => {
                      setFilePath(value);
                      if (filePathError) {
                        setFilePathError('');
                      }
                    }}
                    placeholder={t('generative_answer.file_path_placeholder', 'Choose or enter the file to read. Tags are allowed.')}
                    singleLine
                    tags={tags}
                    className="generative-answer-file-path-editor"
                    onTagClick={(e) => handleGenMenuOpen(e, 'filePath')}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color={filePathError ? 'error' : 'text.secondary'}
                  sx={{ display: 'block', mt: 1 }}
                >
                  {filePathHelperText}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FolderOpenIcon />}
                    onClick={handleFileBrowse}
                    disabled={fileBrowseDisabled}
                    sx={{
                      borderColor: '#7b61ff',
                      color: '#7b61ff',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: 'rgba(123, 97, 255, 0.08)',
                        borderColor: '#7b61ff',
                      }
                    }}
                  >
                    {t('common.browse', 'Browse')}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Tag Prompts Section */}
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #7b61ff10',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#7b61ff',
                    color: 'white',
                    borderRadius: '6px',
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 0.5, sm: 0.7 },
                    mr: { xs: 1, sm: 2 },
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(123, 97, 255, 0.2)',
                  }}>
                    {t('generative_answer.generate_badge', 'TAGS')}
                  </Box>
                  {t('generative_answer.tag_prompts_label', 'Tags to fill')}
                </Typography>
              }
              action={
                <Button
                  onClick={addTagPrompt}
                  startIcon={<AddIcon />}
                  sx={{
                    color: '#7b61ff',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#f8f7ff',
                    },
                  }}
                >
                  {t('generative_answer.add_tag_prompt', 'Add a tag')}
                </Button>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />
            <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
              {tagPrompts.map((tagPrompt, index) => (
                <Card key={tagPrompt.id} sx={{
                  mb: 2,
                  border: '1px solid #e0e0f0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  '&:last-child': { mb: 0 }
                }}>
                  <Box sx={{
                    p: 2,
                    backgroundColor: '#f8f9fc',
                    borderBottom: '1px solid #e0e0f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <Typography variant="subtitle2" sx={{
                      color: '#7b61ff',
                      fontWeight: 600,
                      minWidth: '60px'
                    }}>
                      {t('generative_answer.tag_number_label', 'Tag')} {index + 1}:
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <TagEditor
                        caseid={caseid}
                        id={`gen-tag-${tagPrompt.id}`}
                        aria-describedby={`gen-tag-help-${tagPrompt.id}`}
                        ref={el => tagPromptRefs.current[tagPrompt.id] = el}
                        value={tagPrompt.tag}
                        onChange={(newTag) => updateTagPromptTag(index, newTag)}
                        placeholder={t('generative_answer.tag_placeholder', 'Name of the tag to fill')}
                        singleLine
                        tags={tags}
                        className="generative-answer-output-tag-editor"
                        onTagClick={(e) => handleGenMenuOpen(e, 'tag', index)}
                      />
                      <Typography
                        id={`gen-tag-help-${tagPrompt.id}`}
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1 }}
                      >
                        {t('generative_answer.tag_name_help', 'The answer will be saved in this tag.')}
                      </Typography>
                    </Box>
                    {tagPrompts.length > 1 && (
                      <Tooltip title={t('generative_answer.remove_tag_prompt', 'Remove this tag')}>
                        <IconButton
                          onClick={() => removeTagPrompt(index)}
                          size="small"
                          sx={{
                            color: '#e53e3e',
                            '&:hover': {
                              backgroundColor: 'rgba(229, 62, 62, 0.1)',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{
                      mb: 1,
                      color: '#666',
                      fontWeight: 600
                    }}>
                      {t('generative_answer.prompt_label', 'What should be written?')}
                    </Typography>
                    <TagEditor
                    caseid={caseid}
                      id={`gen-prompt-${tagPrompt.id}`}
                      aria-describedby={`gen-prompt-help-${tagPrompt.id}`}
                      ref={el => promptRefs.current[tagPrompt.id] = el}
                      value={tagPrompt.prompt}
                      onChange={(newVal) => updateTagPromptPrompt(index, newVal)}
                      placeholder={t('generative_answer.prompt_placeholder', `Explain what should be written in ${tagPrompt.tag || 'this tag'}...`)}
                      minHeight="100px"
                      onTagClick={(e) => handleGenMenuOpen(e, 'prompt', index)}
                      tags={tags}
                    />

                    <Typography
                      id={`gen-prompt-help-${tagPrompt.id}`}
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 1 }}
                    >
                      {t('generative_answer.prompt_help', 'Tell the assistant what you want in this tag. You can include other tags if needed.')}
                    </Typography>
                  </Box>
                </Card>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TagsDropdown
        tags={tags}
        anchorEl={genAnchorEl}
        handleTagSelect={handleGenTagSelect}
        setAnchorEl={handleGenMenuClose}
        menuType={genMenuType}
      />
      {actionType === 'generativeanswerfile' && FileBrowserComponent}
    </ActionSettingsLayout>
  );
};

export default ActionsGenerativeAnswer;
